/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Injectable } from '@angular/core';
import {
  OrganizationAllTimeMetrics,
  OrganizationMembersMetrics,
  OrganizationPageSummary,
} from '@app/app/page-modules/admin/components/analytics/admin-analytics.model';
import {
  AssessmentDatapointMetrics,
  AssessmentMemberMetricsSummary,
} from '@app/app/page-modules/resource/components/editor/analytics/assessment-analytics.model';
import { MemberReview } from '@app/app/shared/models';
import { MembersBooleanSearchRequest, ReviewsSearchRequest } from '@app/app/shared/models/admin/boolean-filters.model';
import {
  AccountInvitationsSummary,
  MemberActivity,
  MemberInvitationRequest,
  MemberInvitationResponse,
  MemberRecord,
  MemberRecordLearnerPlaylist,
  MemberRecordLearnerPlaylistCardsResponse,
  MemberRecordPlaylistCardResponse,
  MemberRecordPlaylistsRequest,
  Members,
  MembersInvitationBulkResponse,
  MembersSearchResponse,
  UserCardRecord,
  UserEnrollment,
  UserPlaylistRecord,
  UserSearch,
  ValidateEmailsFromCsvFileResponse,
} from '@app/app/shared/models/admin/members.model';
import { StatsData, StatsSummary } from '@app/app/shared/models/analytics/analytics.model';
import { MembersMessageRequest } from '@app/app/shared/models/messages/messages.model';
import { Page } from '@app/app/shared/models/page';
import { delay } from 'rxjs/operators';
import { ObservableResult } from '../../../../shared/store';
import { MembersDataService, PlaylistMembersPage } from './members-data.service';

@Injectable()
export class MockMembersDataService implements MembersDataService {
  private members = [
    {
      _id: '0',
      createdAt: new Date(),
      email: '',
      externalId: '0',
      lastActiveOn: new Date(),
      firstName: 'firstName',
      lastName: 'lastName',
      organizationId: '0',
      password: '1234564',
      roles: ['Workspace admin'],
      status: '',
    },
  ] as Members[];

  private memberRecord = {
    about: {
      email: 'some@email.com',
      firstName: 'firstName',
      lastName: 'lastName',
      joinedDate: new Date(),
      folio: 'folio',
    },
    thumbnail: '',
    lastActiveOn: new Date(),
    momentsLogged: 10,
    badgesEarned: 10,
    playlistsCompleted: 10,
    roles: [
      {
        type: 'LEARNER',
        _id: '',
        name: [],
      },
    ],
    groups: [
      {
        id: '123456',
        name: 'Group 1',
        type: 'STANDARD',
      },
    ],
  } as MemberRecord;

  private membersSearchResponse = {
    members: [...this.members],
    page: 1,
    pageSize: 1,
    total: 1,
  };

  private invitationsResponse: Page<MemberInvitationResponse> = {
    hasContent: true,
    pageSize: 10,
    totalNumberOfElement: 2,
    currentPageNumber: 0,
    numberOfPages: 1,
    content: [
      {
        _id: 'invite-1',
        email: 'pending.invitee@example.com',
        createdAt: new Date().toISOString(),
        status: { type: 'INVITED', on: new Date().toISOString() },
        sendCounter: 1,
      },
      {
        _id: 'invite-2',
        email: 'accepted.invitee@example.com',
        createdAt: new Date().toISOString(),
        status: { type: 'ACCEPTED', on: new Date().toISOString() },
        sendCounter: 1,
      },
    ],
  };

  addMembersToGroup(userGroupUid: string, request: string[]): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  removeMembersFromGroup(userGroupUid: string, request: string[]): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  loadMemberRecord(userUid?: string, playlistUid?: string): ObservableResult<MemberRecord> {
    return ObservableResult.ofSuccess({ ...this.memberRecord }).pipe(delay(1500));
  }

  getMemberEnrollments(userUid: string): ObservableResult<UserEnrollment[]> {
    return ObservableResult.ofSuccess([]);
  }

  getMemberActivities(memberId: string, page: number, size: number): ObservableResult<Page<MemberActivity>> {
    return ObservableResult.ofSuccess({
      currentPageNumber: page,
      pageSize: size,
      totalNumberOfElement: 0,
      content: [],
    });
  }

  getUserPlaylistRecord(userUid: string, playlistUri: string): ObservableResult<UserPlaylistRecord> {
    return ObservableResult.ofSuccess();
  }

  getUserCardRecord(userUid: string, cardUri: string): ObservableResult<UserCardRecord> {
    return ObservableResult.ofSuccess();
  }

  getLearnerPlaylistCards(learnerPlaylistId: string): ObservableResult<MemberRecordLearnerPlaylistCardsResponse> {
    return ObservableResult.ofSuccess();
  }

  getPlaylistLearnerCardsByUserId(playlistUid: string, userUid: string): ObservableResult<MemberRecordPlaylistCardResponse[]> {
    return ObservableResult.ofSuccess();
  }

  getMemberPlaylistsByType(userUid: string, request: MemberRecordPlaylistsRequest): ObservableResult<Page<MemberRecordLearnerPlaylist>> {
    return ObservableResult.ofSuccess();
  }

  searchNonAdminUsers(page: number, pageSize: number, searchString: string): ObservableResult<Page<UserSearch>> {
    return ObservableResult.ofSuccess();
  }

  searchUsers(page: number, pageSize: number, searchString: string, organization?: string): ObservableResult<Page<UserSearch>> {
    return ObservableResult.ofSuccess();
  }

  getAllInvitationsList(
    page: number,
    pageSize: number,
    email?: string,
    groupUid?: string,
  ): ObservableResult<Page<MemberInvitationResponse>> {
    return ObservableResult.ofSuccess(this.invitationsResponse);
  }

  getAcceptedInvitationsList(page: number, pageSize: number, groupUid?: string): ObservableResult<Page<MemberInvitationResponse>> {
    return ObservableResult.ofSuccess(this.filterInvitationsByStatus('ACCEPTED'));
  }

  getNotAcceptedInvitationsList(page: number, pageSize: number, groupUid?: string): ObservableResult<Page<MemberInvitationResponse>> {
    return ObservableResult.ofSuccess(this.filterInvitationsByStatus('INVITED'));
  }

  private filterInvitationsByStatus(status: 'INVITED' | 'ACCEPTED'): Page<MemberInvitationResponse> {
    const content = this.invitationsResponse.content.filter((invitation) => invitation.status.type === status);
    return { ...this.invitationsResponse, content, totalNumberOfElement: content.length };
  }

  getInvitationsSummary(): ObservableResult<AccountInvitationsSummary> {
    return ObservableResult.ofSuccess({ total: 0, pending: 0, accepted: 0, expired: 0 });
  }

  inviteNewMembers(request: MemberInvitationRequest): ObservableResult<MembersInvitationBulkResponse> {
    return ObservableResult.ofSuccess();
  }

  importNewMembers(
    csvFile: File,
    groupIdsQueryParam: string,
    playlistIdsQueryParam: string,
  ): ObservableResult<MembersInvitationBulkResponse> {
    return ObservableResult.ofSuccess();
  }

  resendInvitation(invitationId: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  validateUsersFromCsvFile(csvFile: File, playlistUid: string): ObservableResult<ValidateEmailsFromCsvFileResponse> {
    return ObservableResult.ofSuccess();
  }

  getLearnerCard(playlistUid: string, userUid: string, cardUid: string): ObservableResult<MemberRecordPlaylistCardResponse> {
    return ObservableResult.ofSuccess();
  }

  getLearnerCardForReviewer(userUid: string, cardUid: string): ObservableResult<MemberRecordPlaylistCardResponse> {
    return ObservableResult.ofSuccess();
  }

  getStatisticsNewMembers(from: string, to: string): ObservableResult<StatsData[]> {
    return ObservableResult.ofSuccess([]);
  }

  getStatisticsActiveMembers(from: string, to: string): ObservableResult<StatsData[]> {
    return ObservableResult.ofSuccess([]);
  }

  getStatisticsSummary(): ObservableResult<StatsSummary> {
    return ObservableResult.ofSuccess({
      totalMembers: 10,
      learners: 10,
      instructors: 10,
      admins: 10,
      publishedPlaylists: 10,
      startedPlaylists: 10,
      completedPlaylists: 10,
      createdBadges: 10,
      issuedBadges: 10,
    });
  }

  searchMembers(request: MembersBooleanSearchRequest, page: number, size: number): ObservableResult<MembersSearchResponse> {
    return ObservableResult.ofSuccess(this.membersSearchResponse);
  }

  searchCardMembers(
    request: MembersBooleanSearchRequest,
    playlistUid: string,
    cardUid: string,
    page: number,
    size: number,
  ): ObservableResult<MembersSearchResponse> {
    return ObservableResult.ofSuccess(this.membersSearchResponse);
  }

  searchStandaloneCardMembers(
    request: MembersBooleanSearchRequest,
    cardUid: string,
    page: number,
    size: number,
  ): ObservableResult<MembersSearchResponse> {
    return ObservableResult.ofSuccess(this.membersSearchResponse);
  }

  searchPlaylistMembers(
    request: MembersBooleanSearchRequest,
    playlistUid: string,
    page: number,
    size: number,
  ): ObservableResult<MembersSearchResponse> {
    return ObservableResult.ofSuccess(this.membersSearchResponse);
  }

  getPlaylistMembers(playlistUid: string, page: number, size: number, term?: string): ObservableResult<PlaylistMembersPage> {
    return ObservableResult.ofSuccess({ members: [], total: 0 });
  }

  booleanExportMembers(request: MembersBooleanSearchRequest): ObservableResult<string> {
    return ObservableResult.ofSuccess();
  }

  booleanExportPlaylistMembers(request: MembersBooleanSearchRequest, playlistUid: string): ObservableResult<string> {
    return ObservableResult.ofSuccess();
  }

  booleanExportCardMembers(request: MembersBooleanSearchRequest, playlistUid: string, cardUid: string): ObservableResult<string> {
    return ObservableResult.ofSuccess();
  }

  booleanExportEventCardMembers(request: MembersBooleanSearchRequest, playlistUid: string, eventUid: string): ObservableResult<string> {
    return ObservableResult.ofSuccess();
  }

  sendMessageToMembers(request: MembersMessageRequest): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  sendMessageToPlaylistMembers(request: MembersMessageRequest, playlistUid: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  sendMessageToCardMembers(request: MembersMessageRequest, playlistUid: string, cardUid: string): ObservableResult<void> {
    return ObservableResult.ofSuccess();
  }

  searchPlaylistReviews(
    request: ReviewsSearchRequest,
    playlistUid: string,
    page: number,
    size: number,
  ): ObservableResult<Page<MemberReview>> {
    return ObservableResult.ofSuccess();
  }

  searchCardReviews(request: ReviewsSearchRequest, cardUid: string, page: number, size: number): ObservableResult<Page<MemberReview>> {
    return ObservableResult.ofSuccess();
  }

  getAssessmentMetrics(
    playlistUid: string,
    cardUid: string,
    from: string,
    to: string,
    request: MembersBooleanSearchRequest,
  ): ObservableResult<AssessmentDatapointMetrics> {
    return ObservableResult.ofSuccess();
  }

  getAssessmentMetricsSummary(
    playlistUid: string,
    cardUid: string,
    request: MembersBooleanSearchRequest,
  ): ObservableResult<AssessmentMemberMetricsSummary> {
    return ObservableResult.ofSuccess();
  }

  getActiveUsersNowCount(): ObservableResult<number> {
    return ObservableResult.ofSuccess();
  }

  getOrganizationAllTimeMetrics(): ObservableResult<OrganizationAllTimeMetrics> {
    return ObservableResult.ofSuccess();
  }

  getOrganizationPagesSummary(): ObservableResult<OrganizationPageSummary[]> {
    return ObservableResult.ofSuccess();
  }

  getOrganizationMembersMetrics(
    request: MembersBooleanSearchRequest,
    from: string,
    to: string,
  ): ObservableResult<OrganizationMembersMetrics> {
    return ObservableResult.ofSuccess();
  }
}
